/**
 * ehr#571 (reopen 10-24) - who reviews a progress note and when they can edit it, kept here as
 * plain functions so they can be tested without the editor.
 *
 * PROGRESS-NOTE V141 (V83): a note waiting for review opens ready to edit for its reviewer: they
 * type in any box and comment on a section straight away, then co-sign or send it back. Before
 * this, the boxes stayed locked under the author's "Locked until returned" banner until the
 * reviewer found Request Changes in the footer.
 */

/**
 * The note's reviewer: never its own author (the API refuses it too); an admin or manager, or the
 * co-signer it was sent to.
 */
export function canReviewNote(o: {
  authorId: string | null;
  currentUserId: string | null;
  assignedCosignerId: string | null;
  /** Admin or manager. */
  canReviewNotes: boolean;
}): boolean {
  if (o.authorId != null && o.authorId === o.currentUserId) return false;
  return (
    o.canReviewNotes || (o.assignedCosignerId != null && o.assignedCosignerId === o.currentUserId)
  );
}

/** The reviewer edits in place as soon as the note is with them (pending), no extra click. */
export function reviewerEditing(o: { status: string; canReview: boolean }): boolean {
  return o.status === 'pending' && o.canReview;
}

/**
 * The reviewer's footer: Co-sign and finalize is the green button until they edit or comment;
 * after that Request changes is (Chase 10-21, ask 6: "request changes should be on the right side,
 * and it should be green, not the co-sign").
 */
export function reviewPrimary(o: { draftCount: number; message: string }): 'cosign' | 'changes' {
  return o.draftCount > 0 || o.message.trim() ? 'changes' : 'cosign';
}

/** Edits in a box, for its header chip ("1 edit", "3 edits"); 0 shows no chip. */
export function editChip(n: number): string | null {
  return n > 0 ? `${n} edit${n === 1 ? '' : 's'}` : null;
}

// ── ehr#571 (reopen 10-24, points 3-5): V141's comment cards, comments on selected words, and
// General comments. Plain functions so the anchoring can be tested without the editor.

/** A comment the reviewer has posted (Comment) but not sent yet; it goes with Request changes. */
export interface DraftComment {
  id: string;
  /** The section's body key, or 'general' for a General comment. */
  section: string;
  text: string;
  /** The words it is on, when the reviewer selected some in the box (V141 V92). */
  quote?: string;
  /** Where those words started in the box's text then. */
  pos?: number;
  at: string;
}

export const GENERAL = 'general';

/** The longest selection a comment keeps (the API's limit). */
export const QUOTE_MAX = 500;

/**
 * The words selected in a box, trimmed of the spaces around them, and where they start; null for
 * an empty or blank selection. Any pick counts, even one letter (V141 V98).
 */
export function selectionQuote(
  value: string,
  start: number,
  end: number,
): { quote: string; pos: number } | null {
  const a = Math.max(0, Math.min(start, end));
  const b = Math.min(value.length, Math.max(start, end));
  const raw = value.slice(a, b);
  const quote = raw.trim();
  if (!quote) return null;
  const pos = a + (raw.length - raw.trimStart().length);
  return { quote: quote.slice(0, QUOTE_MAX), pos };
}

/** Where a comment's words are in the box's text now: at their old place if still there, else the first match. */
export function findAnchor(
  text: string,
  quote: string | undefined,
  pos?: number,
): { start: number; end: number } | null {
  if (!quote) return null;
  if (pos !== undefined && text.slice(pos, pos + quote.length) === quote)
    return { start: pos, end: pos + quote.length };
  const i = text.indexOf(quote);
  return i < 0 ? null : { start: i, end: i + quote.length };
}

export interface Mark {
  start: number;
  end: number;
  cls: string;
}

/**
 * The highlights for a box: one per comment whose words are still in it, never overlapping (the
 * earlier-listed one wins, so the selection being commented on goes first), in text order.
 */
export function anchorMarks(
  text: string,
  items: { quote?: string; pos?: number; cls: string }[],
): Mark[] {
  const out: Mark[] = [];
  for (const it of items) {
    const at = findAnchor(text, it.quote, it.pos);
    if (!at || out.some((m) => at.start < m.end && at.end > m.start)) continue;
    out.push({ ...at, cls: it.cls });
  }
  return out.sort((x, y) => x.start - y.start);
}

/** The box's text cut into plain runs and highlighted runs, for the layer behind the box. */
export function markRuns(text: string, marks: Mark[]): { text: string; cls?: string }[] {
  const runs: { text: string; cls?: string }[] = [];
  let at = 0;
  for (const m of marks) {
    if (m.start < at || m.end > text.length) continue;
    if (m.start > at) runs.push({ text: text.slice(at, m.start) });
    runs.push({ text: text.slice(m.start, m.end), cls: m.cls });
    at = m.end;
  }
  if (at < text.length) runs.push({ text: text.slice(at) });
  return runs;
}

/** The line under the name on a comment card: General, on some words, or on the whole section. */
export function commentSub(section: string, quote?: string): string {
  if (section === GENERAL) return 'General comment';
  if (!quote) return 'Comment on the whole section';
  return `Comment on: ${quote.length > 60 ? `${quote.slice(0, 60).trimEnd()}…` : quote}`;
}

/**
 * What Request changes sends: the section comments as the API takes them, and the General
 * comments as the note to the author (one paragraph each), which the author reads in the
 * changes-requested banner as before.
 */
export function reviewPayload(comments: DraftComment[]): {
  comments: { section: string; text: string; quote?: string; pos?: number }[];
  reason: string;
} {
  const general = comments.filter((c) => c.section === GENERAL && c.text.trim());
  const rest = comments.filter((c) => c.section !== GENERAL && c.text.trim());
  return {
    comments: rest.map((c) => ({
      section: c.section,
      text: c.text.trim(),
      ...(c.quote ? { quote: c.quote, ...(c.pos !== undefined ? { pos: c.pos } : {}) } : {}),
    })),
    reason: general.map((c) => c.text.trim()).join('\n\n'),
  };
}

/**
 * ehr#571 (reopen 10-27): how a part of the note renders while it is in review. Text boxes, and a
 * layout's own boxes (a New Client note's Presenting problem, a Quick add box), are review boxes:
 * the reviewer's copy with "What <author> will see" under it, and the author's Keep / Revert once
 * it is back. Every other part (Session details, goals, diagnosis, interventions, safety,
 * checklists, ratings) stays a set of fields the reviewer can change. Before, only the template's
 * text boxes opened and everything else sat in a disabled fieldset.
 */
export function reviewPartMode(
  kind: string,
  o: { status: string; canReview: boolean; tracked: boolean },
): 'review-box' | 'fields' {
  const inReview = reviewerEditing(o) || o.tracked;
  if (kind === 'text') return 'review-box';
  if (kind === 'box' && inReview) return 'review-box';
  return 'fields';
}

// ── ehr#663 (Chase 10-30): the reviewer's Co-sign & Finalize and Request changes ───────────────

/**
 * What Co-sign & Finalize does: with a required part blank, the list of them opens ("Blank on
 * this note"); with comments not sent yet, it asks first (Co-sign drops comments, keeps edits);
 * otherwise it co-signs.
 */
export function cosignAction(o: {
  blank: number;
  unsentComments: number;
}): 'list' | 'ask' | 'cosign' {
  if (o.blank > 0) return 'list';
  return o.unsentComments > 0 ? 'ask' : 'cosign';
}

/**
 * What Request changes does: sends the note back once there is something to send; with nothing
 * to send but parts blank, the same list opens; with neither, nothing (the button is off).
 */
export function changesAction(o: { blank: number; sendBack: boolean }): 'send' | 'list' | null {
  if (o.sendBack) return 'send';
  return o.blank > 0 ? 'list' : null;
}

/**
 * The note's boxes as the reviewer has them: their edited copy over the author's text, with Why
 * treatment continues (body.necessity) apart, as the required-part check reads it. A box the
 * reviewer filled in is no longer blank (Co-sign keeps their text).
 */
export function reviewerTexts(
  pbody: Record<string, string>,
  draft: Record<string, string>,
  necessity: string,
  necKey = 'necessity',
): { pb: Record<string, string>; nec: string } {
  const pb = { ...pbody };
  for (const [k, v] of Object.entries(draft)) if (k !== necKey) pb[k] = v;
  return { pb, nec: draft[necKey] ?? necessity };
}
